// Cache local + file d'attente résiliente pour l'interface chauffeur (zones sans réseau).
import api from "@/lib/api";

const KEY = (id) => `innova.route.${id}`;
const QUEUE = "innova.queue";
const MAX_ATTEMPTS = 10;                     // abandon après 10 échecs réseau
const BACKOFF_MS = [0, 5e3, 15e3, 30e3, 60e3, 120e3, 300e3];  // repli exponentiel borné à 5 min

export const isOnline = () => (typeof navigator === "undefined" ? true : navigator.onLine);

export function saveRoutePack(assignmentId, pack) {
  try {
    localStorage.setItem(KEY(assignmentId), JSON.stringify({ ...pack, cached_at: Date.now() }));
  } catch { /* quota */ }
}

export function loadRoutePack(assignmentId) {
  try {
    const raw = localStorage.getItem(KEY(assignmentId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE) || "[]"); } catch { return []; }
}

function writeQueue(q) {
  try { localStorage.setItem(QUEUE, JSON.stringify(q.slice(-500))); } catch { /* quota */ }
}

export const queueSize = () => readQueue().length;

const nextDelay = (attempts) => BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];

function enqueue(path, body) {
  const q = readQueue();
  q.push({ path, body, at: new Date().toISOString(), attempts: 0, next_try: Date.now() });
  writeQueue(q);
}

/** POST tout de suite si le réseau répond, sinon mise en file d'attente. */
export async function postOrQueue(path, body, { critical = true } = {}) {
  if (isOnline()) {
    try {
      return await api.post(path, body);
    } catch (e) {
      const retryable = !e.response || e.response.status >= 500 || e.response.status === 429;
      if (!retryable || !critical) throw e;
    }
  }
  if (critical) enqueue(path, body);
  return { data: { queued: true } };
}

/**
 * Renvoie les actions en attente, dans l'ordre. Une action qui échoue pour une
 * raison réseau/serveur est replanifiée avec un repli exponentiel (elle n'est
 * jamais perdue), et abandonnée seulement après MAX_ATTEMPTS ou un refus 4xx.
 */
export async function flushQueue() {
  if (!isOnline()) return 0;
  let q = readQueue();
  let sent = 0;
  const now = Date.now();
  const keep = [];
  for (let i = 0; i < q.length; i += 1) {
    const item = q[i];
    if ((item.next_try || 0) > now) { keep.push(item); continue; }
    try {
      await api.post(item.path, item.body);
      sent += 1;
    } catch (e) {
      const retryable = !e.response || e.response.status >= 500 || e.response.status === 429;
      const attempts = (item.attempts || 0) + 1;
      if (retryable && attempts < MAX_ATTEMPTS) {
        keep.push({ ...item, attempts, next_try: Date.now() + nextDelay(attempts) });
        if (!e.response) {                    // toujours hors réseau: on garde la suite intacte
          keep.push(...q.slice(i + 1));
          break;
        }
      }
      // sinon: refusée par le serveur ou trop d'essais -> abandonnée
    }
  }
  writeQueue(keep);
  return sent;
}

/** Vide la file dès le retour du réseau et toutes les 30 s (appelé une fois). */
let started = false;
export function startAutoFlush(onFlushed) {
  if (started || typeof window === "undefined") return () => {};
  started = true;
  const run = async () => {
    const sent = await flushQueue().catch(() => 0);
    if (sent && onFlushed) onFlushed(sent);
  };
  const timer = setInterval(run, 30000);
  window.addEventListener("online", run);
  run();
  return () => {
    clearInterval(timer);
    window.removeEventListener("online", run);
    started = false;
  };
}
